import { Button, Flex, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import {
  FormTextField,
  FormPhoneInput,
  FormSelect,
  FormPasswordInput,
} from '@shared/ui';
import { USER_FORM_INITIAL_VALUES } from './constants';
import { type UserFormCreateValues, $UserFormCreteValidate } from './types';
import { FormCompanySelect } from '@entities/company';
import {
  ROLES,
  USER_GROUPS_FOR_ADMIN,
  USER_GROUPS_FOR_RECRUITER,
} from '@shared/constants';

type UserCreateForm = {
  isAdmin: boolean;
  isLoading: boolean;
  isDisabled: boolean;
  initialValues?: UserFormCreateValues;
  handleSubmit: (values: UserFormCreateValues) => void;
  handleClose: () => void;
};

export const UserCreateForm = ({
  isAdmin,
  isLoading,
  isDisabled,
  initialValues = USER_FORM_INITIAL_VALUES,
  handleSubmit,
  handleClose,
}: UserCreateForm) => {
  const form = useForm<UserFormCreateValues>({
    initialValues,
    validate: zodResolver($UserFormCreteValidate),
  });

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Title order={4} c='primary.7'>
        Личные данные
      </Title>

      <FormTextField
        title='Фамилия'
        description='Фамилия пользователя'
        placeholder='Введите фамилию'
        key={form.key('lastName')}
        {...form.getInputProps('lastName')}
        error={form.errors.lastName}
      />
      <FormTextField
        title='Имя'
        description='Имя пользователя'
        placeholder='Введите имя'
        key={form.key('firstName')}
        {...form.getInputProps('firstName')}
        error={form.errors.firstName}
      />
      <FormTextField
        title='Email'
        description='Контактный Email'
        placeholder='example@test.ru'
        key={form.key('email')}
        {...form.getInputProps('email')}
        error={form.errors.email}
      />
      <FormPhoneInput
        title='Контактный телефон'
        description='Номер телефона для связи'
        placeholder='+'
        key={form.key('phone')}
        value={form.values.phone}
        onChange={value => form.setFieldValue('phone', value)}
        error={form.errors.phone}
      />
      <FormSelect
        title='Группа'
        description='Ролевая группа'
        placeholder='Выберите ролевую группу'
        key={form.key('role')}
        {...form.getInputProps('role')}
        data={isAdmin ? USER_GROUPS_FOR_ADMIN : USER_GROUPS_FOR_RECRUITER}
        error={form.errors.role}
      />
      {(form.values.role === ROLES.RECRUITER ||
        form.values.role === ROLES.MANAGER) && (
        <FormCompanySelect
          placeholder='Выберите компанию'
          description='Компания, сотрудником которой является пользователь'
          key={form.key('companyId')}
          {...form.getInputProps('companyId')}
          error={form.errors.companyId}
        />
      )}

      <Title order={4} c='primary.7' mt={16}>
        Системные данные
      </Title>

      <FormPasswordInput
        title='Пароль'
        description='Пароль для входа в систему'
        placeholder='Введите пароль'
        key={form.key('password')}
        {...form.getInputProps('password')}
        error={form.errors.password}
      />

      <FormPasswordInput
        title='Подтверждение пароля'
        description='Подтверждение пароля'
        placeholder='Введите пароль'
        key={form.key('passwordConfirmation')}
        {...form.getInputProps('passwordConfirmation')}
        error={form.errors.passwordConfirmation}
      />

      <Flex gap={8} mt={16}>
        <Button variant='gray' onClick={handleClose}>
          Назад
        </Button>
        <Button type='submit' loading={isLoading} disabled={isDisabled}>
          Создать
        </Button>
      </Flex>
    </form>
  );
};
